import { classifyProductArea } from '@layers/web/utils/product-area.ts'
import {
  trackGtag,
  trackClarity,
  waitForConsentUpdate,
  getUserConsentStatus,
} from '@layers/web/utils/tracking.ts'
import type { ProductArea } from '@layers/web/utils/types/product-area.ts'

const SESSION_KEY_SEEN_AREAS = 'rtg_seen_product_areas'
const SESSION_KEY_ENTRY_SENT = 'rtg_product_entry_sent'
const SESSION_KEY_CROSSOVER_SENT = 'rtg_crossover_sent'
const SESSION_KEY_MODE_SWITCHES = 'rtg_mode_switches'

export const useProductArea = () => {
  const localeStore = useLocaleStore()
  const { urls: localeURLs } = storeToRefs(localeStore)

  /**
   * Session state is analytics data and is only written with performance consent.
   *
   * Reads `window.userConsent` fresh instead of `useUserConsent()`'s ref, which is
   * a snapshot taken when the composable was created. No caller in the repo uses
   * `useUserConsent(true)`, and the reactive variant relies on `onBeforeMount` —
   * which never runs inside a plugin.
   */
  const mayMeasureProductArea = (): boolean => {
    return !!getUserConsentStatus().performance
  }

  /**
   * The product areas the session has touched, in visit order. Only non-neutral
   * areas are recorded, since neutral pages never count as crossover.
   */
  const getSeenProductAreas = (): ProductArea[] => {
    const raw = tryGetSessionStorage(SESSION_KEY_SEEN_AREAS)
    if (!raw) {
      return []
    }

    return String(raw).split(',') as ProductArea[]
  }

  const getCurrentProductArea = (): ProductArea | null => {
    if (import.meta.server) {
      return null
    }

    return classifyProductArea(window.location.pathname, localeURLs.value)
  }

  const getModeSwitchCount = (): number => {
    return Number(tryGetSessionStorage(SESSION_KEY_MODE_SWITCHES) ?? 0)
  }

  /**
   * Discrete buckets instead of an exact count, so Clarity's filter UI stays
   * usable. The accumulation is harmless because `3+` is terminal — a session
   * carrying `3+` is filtered correctly even if it also carries `1` and `2`.
   */
  const getModeSwitchBucket = (count: number): string => {
    if (count >= 3) {
      return '3+'
    }

    return String(count)
  }

  const incrementModeSwitchCount = (): number => {
    const next = getModeSwitchCount() + 1

    if (!mayMeasureProductArea()) {
      return next
    }

    trySetSessionStorage(SESSION_KEY_MODE_SWITCHES, String(next))
    trackClarity('set', 'mode_switches', getModeSwitchBucket(next))

    return next
  }

  /**
   * Clarity's custom tags ACCUMULATE — they are not overwritten. `product_area`
   * therefore becomes the set of areas whose pages were viewed in the recording,
   * which is exactly what the filters answer: "all solresor", and — by AND-ing both
   * values (VariablesAnd) in the dashboard — recordings that show both products. This
   * is recording-scoped, so it can't mislabel a rolfsbuss-only recording as mixed.
   */
  const tagClarityProductArea = (area: ProductArea): void => {
    trackClarity('set', 'product_area', area)
  }

  /**
   * Measuring point 2 (session exclusivity), the queryable half. Fires once per
   * session the first time the session has seen BOTH products, so GA4 can count
   * crossover sessions as a single-event presence. Solresor-only sessions are then
   * "sessions that reached solresor" minus these — the per-session exclusion the
   * Data API cannot express as a segment. Flag written before the event, like
   * product_entry, so an unsendable event never re-fires.
   */
  const sendCrossoverEventOnce = (seenAreas: ProductArea[]): void => {
    const hasSeenBothProducts = seenAreas.includes('solresor')
      && seenAreas.includes('gruppresor')

    if (!hasSeenBothProducts || tryGetSessionStorage(SESSION_KEY_CROSSOVER_SENT)) {
      return
    }

    trySetSessionStorage(SESSION_KEY_CROSSOVER_SENT, '1')

    trackGtag('event', 'session_crossover', {})
  }

  /**
   * Returns the areas the session has seen *including* this one, so crossover is
   * detected in the same page view that makes the session mixed.
   */
  const rememberSeenArea = (area: ProductArea): ProductArea[] => {
    const seenAreas = getSeenProductAreas()
    if (seenAreas.includes(area)) {
      return seenAreas
    }

    const updatedAreas = [...seenAreas, area]
    trySetSessionStorage(SESSION_KEY_SEEN_AREAS, updatedAreas.join(','))

    return updatedAreas
  }

  /**
   * Measuring point 1. Fires once per session, so event count = session count.
   * The deduplication flag is written before the event is sent, since an event we
   * cannot deduplicate is worse than no event at all.
   */
  const sendProductEntryOnce = (area: ProductArea, path: string): void => {
    if (tryGetSessionStorage(SESSION_KEY_ENTRY_SENT)) {
      return
    }

    trySetSessionStorage(SESSION_KEY_ENTRY_SENT, '1')

    trackGtag('event', 'product_entry', {
      product_area: area,
      landing_page: path,
    })
  }

  /**
   * Records a page view in the session state and sends product_entry on the first
   * non-neutral page view.
   *
   * Waits for the consent response the same way `trackGtag` does, so a first-time
   * visitor who accepts cookies after the landing page still gets their
   * product_entry. Called after `trackPageView` and sends its event afterwards,
   * since both queue behind the same consent promise in call order.
   */
  const registerPageView = async (path: string): Promise<void> => {
    await waitForConsentUpdate()

    if (!mayMeasureProductArea()) {
      return
    }

    const area = classifyProductArea(path, localeURLs.value)
    if (!area || area === 'neutral') {
      return
    }

    const seenAreas = rememberSeenArea(area)

    tagClarityProductArea(area)
    sendCrossoverEventOnce(seenAreas)

    sendProductEntryOnce(area, path)
  }

  return {
    registerPageView,
    getCurrentProductArea,
    getSeenProductAreas,
    getModeSwitchCount,
    incrementModeSwitchCount,
  }
}
